import type { FlightDeckEvent, MigrationDevice } from '../api/migrations';

// Where is this device in its migration (FD-457)?
//
// The mockups draw a six-step stepper. Nothing on the wire carries a "phase", so
// it is DERIVED — and the derivation is here, unit-tested, rather than inside the
// component, because two readers of the same rule will eventually disagree.
//
// THE INPUTS ARE REAL AND NARROW:
//
//   * progress rows in FlightDeckEvents have `rowKey` = `progress-<actionId>`
//     (api/WriteTable stamps it deterministically so a repeat report is
//     idempotent by (jobId, actionId)), and the FD-ADR-025 actionId vocabulary
//     is stage-prefixed: PRE- / PSH- / ENR- / ARR- / FIN-.
//   * `status` and `terminalOutcome` on the device row. READ BOTH — FD-345 made
//     them separate columns precisely so a regressed `status` under a surviving
//     `terminalOutcome` stays detectable, and FD-362's heal-on-read keys off it.
//   * `readinessStatus` / `readinessOverride` for the first step.
//
// WHAT IT DOES NOT DO: invent timestamps, or claim a step "passed" when all it
// knows is that a later step started. A phase with no evidence is `pending`.

export type PhaseState = 'pending' | 'current' | 'done' | 'failed' | 'stopped';

export interface DevicePhase {
  id: string;
  label: string;
  state: PhaseState;
  /** A short timestamp or reason. Absent when there is nothing true to say. */
  detail?: string;
}

export interface DevicePhaseView {
  phases: DevicePhase[];
  /** Index of the furthest phase with evidence; -1 before anything happened. */
  reachedIndex: number;
}

/** Stage prefix → stepper index. The FD-ADR-025 vocabulary, frozen at sign-off. */
const PREFIX_TO_INDEX: Record<string, number> = {
  PRE: 1, // Flight Plan — preflight actions
  PSH: 2, // Pushback
  ENR: 3, // Enroute
  ARR: 4, // Arrival
  FIN: 5, // Complete — the finalizer
};

const LABELS = ['Ready', 'Flight Plan', 'Pushback', 'Enroute', 'Arrival', 'Complete'];

/**
 * The six stage labels, in order, so a distribution can name a phase without
 * keeping a second copy of the vocabulary. Frozen because a caller that mutated
 * the array would silently re-label the stepper for every other reader.
 */
export const PHASE_LABELS: readonly string[] = Object.freeze([...LABELS]);

/**
 * THE SAME STAGE TABLE, READ FROM ONE ACTION ID (FD-498).
 *
 * `deriveDevicePhase` above takes an EVENT LIST, because that is what a device
 * detail page has. The campaign grid and the phase distribution have something
 * narrower and much cheaper: `DeviceMigrations.LastActionId`, the single column
 * FD-496 stamps and FD-497 projects. THAT IS THE ENTIRE REASON THOSE TWO TASKS
 * EXIST — deriving a step per device from events would be one partition query
 * per device, and a 500-device wave would fan out 500 of them on one page load.
 *
 * So this is an addition BESIDE the event-driven path, reading the same
 * `PREFIX_TO_INDEX` table. It is deliberately not a second implementation: a
 * second stage table is how the grid's Step column and the stepper come to
 * disagree about what `ARR-` means.
 *
 * `null` for every input the table cannot place — absent, empty, or a stage
 * prefix this console has not been taught. AN UNRECOGNISED PREFIX IS IGNORED
 * RATHER THAN DEFAULTED, which is the rule `deriveDevicePhase` already follows:
 * a new stage must not silently advance a stepper, and it must not land in the
 * wrong column of a distribution either.
 */
export function phaseIndexForActionId(actionId: string | null | undefined): number | null {
  if (typeof actionId !== 'string') return null;
  const prefix = actionId.trim().split('-')[0]?.toUpperCase() ?? '';
  const index = PREFIX_TO_INDEX[prefix];
  return index === undefined ? null : index;
}

/** The phase label for one action id, or `null` when the table cannot place it. */
export function phaseLabelForActionId(actionId: string | null | undefined): string | null {
  const index = phaseIndexForActionId(actionId);
  return index === null ? null : LABELS[index];
}

const TERMINAL_FAILURES = new Set(['salvaged', 'faulted']);

function normalise(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

/** The actionId behind a progress event row, or null if it is not one. */
export function progressActionId(event: FlightDeckEvent): string | null {
  const rowKey = (event as { rowKey?: string }).rowKey;
  if (typeof rowKey === 'string' && rowKey.startsWith('progress-')) {
    return rowKey.slice('progress-'.length);
  }
  // Older rows, and any projection that does not carry rowKey, still expose the
  // actionId in the event payload. Falling back keeps a device whose events
  // predate the convention from rendering as if nothing had happened.
  const actionId = (event as { actionId?: unknown }).actionId;
  return typeof actionId === 'string' && actionId.length > 0 ? actionId : null;
}

function shortDate(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function deriveDevicePhase(
  device: MigrationDevice,
  events: FlightDeckEvent[] = [],
): DevicePhaseView {
  const status = normalise(device.status);
  const outcome = normalise(device.terminalOutcome);

  // Which stages have evidence. An unrecognised prefix is IGNORED rather than
  // mapped to a default: a new stage the console has not been taught about must
  // not silently advance the stepper.
  let reached = -1;
  let lastEventIso: string | null = null;
  for (const event of events) {
    const actionId = progressActionId(event);
    if (!actionId) continue;
    const index = PREFIX_TO_INDEX[actionId.split('-')[0]?.toUpperCase() ?? ''];
    if (index === undefined) continue;
    if (index > reached) reached = index;
    if (!lastEventIso || (event.timeStamp ?? '') > lastEventIso) {
      lastEventIso = event.timeStamp ?? null;
    }
  }

  // Step 0 is readiness, not a progress event: a device is "Ready" once the
  // server has a verdict it would deliver on, or once an operator has waived it.
  const readinessDone =
    device.readinessStatus === 'pass' ||
    device.readinessStatus === 'warn' ||
    device.readinessOverride !== null;
  if (readinessDone && reached < 0) reached = 0;

  const completed = outcome === 'completed' || status === 'completed';
  const failed = TERMINAL_FAILURES.has(outcome) || TERMINAL_FAILURES.has(status);
  const aborted = outcome === 'aborted' || status === 'aborted';
  if (completed) reached = 5;

  const phases: DevicePhase[] = LABELS.map((label, index) => {
    let state: PhaseState = 'pending';
    if (index < reached) state = 'done';
    else if (index === reached) {
      if (completed) state = 'done';
      else if (failed) state = 'failed';
      else if (aborted) state = 'stopped';
      else state = 'current';
    }
    return { id: label.toLowerCase().replace(/\s+/g, '-'), label, state };
  });

  // Detail lines, only where a real value exists.
  if (readinessDone && device.readinessEvaluatedUtc) {
    phases[0].detail = shortDate(device.readinessEvaluatedUtc);
  }
  if (completed && device.terminalUtc) {
    phases[5].detail = shortDate(device.terminalUtc);
  } else if ((failed || aborted) && reached >= 0) {
    phases[reached].detail = failed ? (outcome || status) : 'stopped';
  } else if (reached > 0 && reached < 5 && lastEventIso) {
    phases[reached].detail = shortDate(lastEventIso);
  }

  return { phases, reachedIndex: reached };
}
